import React, { useState, useEffect } from "react";
import { 
  listTiposVenta, 
  createVentaTipo, 
  updateVentaTipo 
} from "../api/ventasGeneral";
import { showAlertError, showAlertExito } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiPlus, FiImage, FiEdit3, FiUpload, FiSettings
} from "react-icons/fi";

const TiposVenta = () => {
  const [loading, setLoading] = useState(true);
  const [tipos, setTipos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    tipo: "",
    logo: "",
    status: 1
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listTiposVenta();
      setTipos(res?.data || []);
    } catch (error) {
      console.error("Error al cargar tipos de venta:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (tipo = null) => {
    if (tipo) {
      setForm({ 
        tipo: tipo.tipo, 
        logo: tipo.logo,
        status: tipo.status
      });
      setCurrentId(tipo.id);
      setEditMode(true);
      setPhotoFile(null);
      if (tipo.logo) {
        setPhotoPreview(process.env.REACT_APP_RUTA_PRODUCTOS + tipo.logo);
      } else {
        setPhotoPreview(null);
      }
    } else {
      setForm({ tipo: "", logo: "", status: 1 });
      setEditMode(false);
      setPhotoPreview(null);
      setPhotoFile(null);
    }
    setShowModal(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setForm(prev => ({ ...prev, logo: file.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("tipo", form.tipo);
      formData.append("status", form.status);
      if (photoFile) {
        formData.append("photo", photoFile);
      }

      if (editMode) {
        await updateVentaTipo(currentId, formData);
        showAlertExito("Éxito", "Tipo de venta actualizado");
      } else {
        await createVentaTipo(formData);
        showAlertExito("Éxito", "Tipo de venta creado");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showAlertError("Error", "No se pudo guardar");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && tipos.length === 0) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden animate-fade-in">
      
      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-xl shadow-indigo-100">
            <FiSettings size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 leading-none tracking-tight">Tipos de Venta</h2>
            <p className="text-sm text-slate-400 mt-1 font-medium">Gestiona plataformas y métodos de entrega</p>
          </div>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-200 active:scale-95"
        >
          <FiPlus size={20} /> Nuevo Tipo
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tipos.map((t) => (
            <div
              key={t.id}
              className="group relative bg-white rounded-[2.5rem] p-6 border-2 border-slate-100 transition-all duration-500 hover:shadow-2xl flex flex-col items-center justify-center"
            >
              <button 
                onClick={() => handleOpenModal(t)}
                className="absolute top-4 right-4 p-2 rounded-xl bg-slate-50 text-slate-300 hover:bg-indigo-50 hover:text-indigo-600 transition-all opacity-0 group-hover:opacity-100"
              >
                <FiEdit3 size={14} />
              </button>

              <div className="w-24 h-24 rounded-3xl bg-slate-50 flex items-center justify-center mb-4 transition-all duration-500 transform group-hover:scale-110 overflow-hidden border border-slate-100">
                {t.logo ? (
                  <img 
                    src={`${process.env.REACT_APP_RUTA_PRODUCTOS}${t.logo}`} 
                    alt={t.tipo}
                    className="w-full h-full object-contain p-2"
                    onError={(e) => { e.target.onerror = null; e.target.src = ""; }}
                  />
                ) : (
                  <FiImage size={32} className="text-slate-200" />
                )}
              </div>

              <div className="text-center">
                <h4 className="text-lg font-black text-slate-800">{t.tipo}</h4>
                <span className={`mt-2 inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${String(t.status) === '1' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                  {String(t.status) === '1' ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoom-in">
              <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-indigo-600 text-white">
                 <h4 className="font-extrabold text-lg flex items-center gap-2">
                   {editMode ? <FiEdit3 /> : <FiPlus />}
                   {editMode ? "Editar Tipo" : "Nuevo Tipo"}
                 </h4>
                 <button onClick={() => setShowModal(false)} className="hover:rotate-90 transition-transform"><FiPlus className="rotate-45" size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                 {/* Imagen */}
                 <div className="flex flex-col items-center gap-4 py-2">
                    <div className="w-24 h-24 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                       {photoPreview ? (
                         <img src={photoPreview} alt="Preview" className="w-full h-full object-contain p-2" />
                       ) : (
                         <FiImage size={32} className="text-slate-200" />
                       )}
                    </div>
                    <label className="cursor-pointer bg-white border border-slate-200 px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2">
                       <FiUpload /> {editMode ? "Cambiar Logo" : "Subir Logo"}
                       <input type="file" className="hidden" accept="image/*" onChange={handlePhotoChange} />
                    </label>
                 </div>

                 <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Nombre del Tipo</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ej: Pedidos Ya, Indrive..."
                      value={form.tipo}
                      onChange={(e) => setForm({...form, tipo: e.target.value})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-indigo-400 outline-none transition-all font-bold"
                    />
                 </div>

                 <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Estado</label>
                    <select 
                      value={form.status}
                      onChange={(e) => setForm({...form, status: Number(e.target.value)})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-indigo-400 outline-none transition-all font-bold"
                    >
                      <option value={1}>Activo</option>
                      <option value={0}>Inactivo</option>
                    </select>
                 </div>

                 <button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all mt-4 disabled:opacity-50"
                 >
                    {submitting ? "Guardando..." : (editMode ? "Actualizar" : "Registrar")}
                 </button>
              </form>
           </div>
        </div>
      )}

    </div>
  );
};

export default TiposVenta;
